// src/components/ui/WalletHeader.jsx
//
// Page header for the student wallet page — same glass-card language
// as CartHeader (gradient backdrop, frosted white panel, icon tile,
// gradient title, chips), AND the exact same self-contained outer
// wrapper CartHeader uses for its own width/margin
// (mx-auto max-w-8xl px-2 sm:px-4 md:px-6 lg:px-8 py-3) — that's what
// OnlineClassHeader/CourseInfo rely on too when rendered with no
// wrapper of their own in OnlineClass.js, so matching it here (rather
// than leaving this header fully edge-to-edge) is what actually lines
// up the width and top spacing with that page. Row layout (icon+title
// beside the chips) only applies at `sm:` and up, with chips wrapping
// rather than forced onto one line, so it still stacks cleanly on a
// narrow phone viewport.
import React from 'react';

const WalletHeader = ({
  title = 'הארנק שלי',
  subtitle = 'קרידיט כללי לשימוש חופשי, וקרידיט גפ״ן לפי תוכנית',
  programsCount,
  totalCredit,
  currency = '₪',
}) => {
  const hasChips = Number.isFinite(programsCount) || Number.isFinite(totalCredit);

  return (
    <section className="mx-auto max-w-8xl px-2 sm:px-4 md:px-6 lg:px-8 py-3" dir="rtl">
      <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50" />

        <div className="relative bg-white/80 backdrop-blur-xl border border-white/60 rounded-2xl sm:rounded-3xl p-4 sm:p-5 lg:p-6 shadow-2xl shadow-indigo-100/50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            {/* Icon + text */}
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center shadow-md">
                <svg
                  viewBox="0 0 24 24"
                  className="w-5 h-5 sm:w-6 sm:h-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  aria-hidden="true"
                >
                  <rect x="2.5" y="5.5" width="19" height="13" rx="2.2" />
                  <path d="M2.5 9.5h19" />
                  <circle cx="16.5" cy="14.5" r="1.4" />
                </svg>
              </div>

              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-indigo-800 to-purple-800 bg-clip-text text-transparent leading-tight">
                  {title}
                </h1>
                {subtitle && (
                  <p className="text-sm sm:text-base text-gray-600 mt-0.5 sm:mt-1 line-clamp-2 sm:truncate">
                    {subtitle}
                  </p>
                )}
              </div>
            </div>

            {/* Chips — wrap rather than forced onto one line, so they
                still hold up if this header is ever squeezed into a
                narrower wrapper again. */}
            {hasChips && (
              <div className="flex-shrink-0 flex flex-wrap items-center gap-1.5 sm:gap-2">
                {Number.isFinite(programsCount) && (
                  <span className="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 px-2.5 sm:px-3 py-1 text-xs sm:text-sm whitespace-nowrap">
                    {programsCount} תוכניות
                  </span>
                )}
                {Number.isFinite(totalCredit) && (
                  <span className="inline-flex items-center rounded-full bg-purple-50 text-purple-700 border border-purple-100 px-2.5 sm:px-3 py-1 text-xs sm:text-sm whitespace-nowrap">
                    סה״כ קרידיט: {currency}
                    {totalCredit.toLocaleString('he-IL', { maximumFractionDigits: 2 })}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WalletHeader;